/** Match http(s) URLs in plain text (conservative; no angle brackets or quotes). */
export const HTTPS_URL_IN_TEXT_REGEX = /\bhttps?:\/\/[^\s<>"']+/gi;

const TRAILING_PUNCT_REGEX = /[.,;:!?)\]]+$/;

const CONTROL_OR_UNSAFE_CHARS = /[\u0000-\u001f\u007f<>"']/;

const MAX_URL_LENGTH = 2048;

const SKIP_ANCESTOR_TAGS = new Set(['a', 'code', 'pre', 'script', 'style', 'textarea']);

export function stripTrailingPunctuationFromUrl(url: string): {
  hrefPart: string;
  trailing: string;
} {
  let hrefPart = url;
  let trailing = '';
  while (hrefPart.length > 0) {
    const match = hrefPart.match(TRAILING_PUNCT_REGEX);
    if (!match) {
      break;
    }
    const candidate = hrefPart.slice(0, -match[0].length);
    if (!validateHttpsUrl(candidate)) {
      break;
    }
    trailing = match[0] + trailing;
    hrefPart = candidate;
  }
  return { hrefPart, trailing };
}

/**
 * Returns a canonical http(s) href or null if the candidate is unsafe.
 */
export function validateHttpsUrl(candidate: string): string | null {
  const trimmed = candidate.trim();
  if (!trimmed || trimmed.length > MAX_URL_LENGTH) {
    return null;
  }
  if (CONTROL_OR_UNSAFE_CHARS.test(trimmed)) {
    return null;
  }

  const { hrefPart } = stripTrailingPunctuationFromUrl(trimmed);
  if (!hrefPart) {
    return null;
  }

  try {
    const parsed = new URL(hrefPart);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    if (parsed.username || parsed.password) {
      return null;
    }
    if (!parsed.hostname) {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}

function shouldSkipTextNode(textNode: Text): boolean {
  let parent: Node | null = textNode.parentNode;
  while (parent) {
    const tag = (parent as Element).tagName?.toLowerCase();
    if (tag && SKIP_ANCESTOR_TAGS.has(tag)) {
      return true;
    }
    parent = parent.parentNode;
  }
  return false;
}

function createAutolink(href: string, label: string): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.textContent = label;
  anchor.className = 'cms-autolink';
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  return anchor;
}

function linkifyTextNode(textNode: Text): void {
  const text = textNode.nodeValue || '';
  HTTPS_URL_IN_TEXT_REGEX.lastIndex = 0;
  if (!HTTPS_URL_IN_TEXT_REGEX.test(text)) {
    return;
  }
  HTTPS_URL_IN_TEXT_REGEX.lastIndex = 0;

  const frag = document.createDocumentFragment();
  let lastIndex = 0;

  text.replace(HTTPS_URL_IN_TEXT_REGEX, (rawMatch, offset) => {
    const { hrefPart, trailing } = stripTrailingPunctuationFromUrl(rawMatch);
    const href = validateHttpsUrl(hrefPart);
    if (!href) {
      return rawMatch;
    }

    if (offset > lastIndex) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
    }

    const label = rawMatch.slice(0, rawMatch.length - trailing.length);
    frag.appendChild(createAutolink(href, label));
    if (trailing) {
      frag.appendChild(document.createTextNode(trailing));
    }

    lastIndex = offset + rawMatch.length;
    return rawMatch;
  });

  if (lastIndex < text.length) {
    frag.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  if (frag.childNodes.length > 0) {
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}

/**
 * Turn plain https?:// text in an already-sanitized HTML subtree into safe anchors.
 * Call after sanitizeHTML and rendering into the DOM (display only; do not persist).
 */
export function linkifyHttpsUrlsInElement(root: HTMLElement): void {
  if (typeof document === 'undefined') {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const toProcess: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    if (shouldSkipTextNode(textNode)) {
      continue;
    }
    const value = textNode.nodeValue || '';
    HTTPS_URL_IN_TEXT_REGEX.lastIndex = 0;
    if (HTTPS_URL_IN_TEXT_REGEX.test(value)) {
      toProcess.push(textNode);
    }
  }

  for (const textNode of toProcess) {
    linkifyTextNode(textNode);
  }
}

export function escapeHtmlForAutolink(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * String-based linkify for tests and non-DOM environments.
 * Only transforms text segments outside HTML tags.
 */
export function linkifyHttpsUrlsInHtml(html: string): string {
  if (!html) {
    return '';
  }

  const parts = html.split(/(<[^>]+>)/);
  return parts
    .map((part) => {
      if (!part || part.startsWith('<')) {
        return part;
      }
      return part.replace(HTTPS_URL_IN_TEXT_REGEX, (rawMatch) => {
        const { hrefPart, trailing } = stripTrailingPunctuationFromUrl(rawMatch);
        const href = validateHttpsUrl(hrefPart);
        if (!href) {
          return rawMatch;
        }
        const label = rawMatch.slice(0, rawMatch.length - trailing.length);
        const safeHref = escapeHtmlForAutolink(href);
        const safeLabel = escapeHtmlForAutolink(label);
        const safeTrailing = escapeHtmlForAutolink(trailing);
        return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="cms-autolink">${safeLabel}</a>${safeTrailing}`;
      });
    })
    .join('');
}
